// src/components/EditableExportsTable.js
import React, { useState, useMemo } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, Paper, TextField, TablePagination, Stack, Typography, Chip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';

// Helper functions
const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp.seconds * 1000).toLocaleString('vi-VN');
};

const getOrderStatusChip = (status) => {
    switch (status) {
        case 'shipped': return <Chip label="Đã chuyển" color="success" size="small" />;
        case 'cancelled': return <Chip label="Đã hủy" color="error" size="small" />;
        case 'other': return <Chip label="Khác" color="default" size="small" />;
        case 'processing': default: return <Chip label="Đang xử lý" color="info" size="small" />;
    }
};

const getOrderStatusLabel = (status) => {
    switch (status) {
        case 'shipped': return "Đã chuyển";
        case 'cancelled': return "Đã hủy";
        case 'other': return "Khác";
        case 'processing': default: return "Đang xử lý";
    }
};


function EditableExportsTable({ exports, onEdit, onDelete, canEdit, canDelete }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    const processedExports = useMemo(() => {
        return (exports || []).filter(item => {
            const term = searchTerm.toLowerCase();
            return (
                item.soNumber?.toLowerCase().includes(term) ||
                item.customerName?.toLowerCase().includes(term) ||
                item.driverName?.toLowerCase().includes(term) ||
                item.vehiclePlate?.toLowerCase().includes(term) ||
                item.createdBy?.toLowerCase().includes(term) ||
                item.approvedBy?.toLowerCase().includes(term)
            );
        });
    }, [exports, searchTerm]);

    const handleExportExcel = () => {
        if (!processedExports || processedExports.length === 0) {
            alert("Không có dữ liệu để xuất.");
            return;
        }
        const dataToExport = processedExports.map(item => ({
            "Ngày Xuất": formatDateTime(item.exportDate),
            "Mã SO": item.soNumber,
            "Khách Hàng": item.customerName,
            "Tài xế": item.driverName,
            "Biển số xe": item.vehiclePlate,
            "Người tạo": item.createdBy,
            "Người duyệt": item.approvedBy || 'Chưa duyệt',
            "Trạng thái ĐH": getOrderStatusLabel(item.status),
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "PhieuXuatDaDuyet");
        XLSX.writeFile(wb, `PhieuXuatDaDuyet_${new Date().toLocaleDateString('vi-VN').replace(/\//g, '-')}.xlsx`);
    };
    
    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box>
            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" spacing={2} justifyContent="space-between">
                    <TextField 
                        label="Tìm kiếm theo Mã SO, Tên KH, Vận chuyển, Người tạo/duyệt..." 
                        variant="outlined" 
                        size="small" 
                        fullWidth
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportExcel}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        Xuất Excel
                    </Button>
                </Stack>
            </Paper>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ngày Xuất</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Mã SO</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Khách Hàng</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Vận chuyển</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Người tạo</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Người duyệt</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Trạng thái ĐH</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Thao tác</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {processedExports.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(item => (
                            <TableRow key={item.id} hover>
                                <TableCell>{formatDateTime(item.exportDate)}</TableCell>
                                <TableCell>{item.soNumber}</TableCell>
                                <TableCell>{item.customerName}</TableCell>
                                <TableCell>
                                    <Typography variant="caption" component="div">TX: {item.driverName || 'N/A'}</Typography>
                                    <Typography variant="caption" component="div">Xe: {item.vehiclePlate || 'N/A'}</Typography>
                                </TableCell>
                                <TableCell>{item.createdBy}</TableCell>
                                <TableCell>{item.approvedBy}</TableCell>
                                <TableCell>{getOrderStatusChip(item.status)}</TableCell>
                                <TableCell align="right">
                                    {canEdit && (
                                        <Tooltip title="Chỉnh sửa">
                                            <IconButton onClick={() => onEdit(item)} color="warning" size="small">
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {canDelete && (
                                        <Tooltip title="Hủy phiếu (Hoàn kho)">
                                            <IconButton onClick={() => onDelete(item)} color="error" size="small">
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={processedExports.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Box>
    );
}

export default EditableExportsTable;
